import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import { apiCall, reportAPI, recruiterAPI, notificationAPI, studentAPI, aiAPI } from '../services/api';
import Icon from '../components/Icon';
import Pagination from '../components/Pagination';
import ReportViewer, { ReportCards } from '../components/ReportViewer';
import CommsAnalyticsDashboard from '../components/CommsAnalyticsDashboard';

const kenyanCountiesData = [
  { name: "Mombasa", code: 1, capital: "Mombasa City" },
  { name: "Kwale", code: 2, capital: "Kwale Town" },
  { name: "Kilifi", code: 3, capital: "Kilifi Town" },
  { name: "Tana River", code: 4, capital: "Hola" },
  { name: "Lamu", code: 5, capital: "Lamu Town" },
  { name: "Taita-Taveta", code: 6, capital: "Voi" },
  { name: "Garissa", code: 7, capital: "Garissa Town" },
  { name: "Wajir", code: 8, capital: "Wajir Town" },
  { name: "Mandera", code: 9, capital: "Mandera Town" },
  { name: "Marsabit", code: 10, capital: "Marsabit Town" },
  { name: "Isiolo", code: 11, capital: "Isiolo Town" },
  { name: "Meru", code: 12, capital: "Meru Town" },
  { name: "Tharaka-Nithi", code: 13, capital: "Chuka" },
  { name: "Embu", code: 14, capital: "Embu Town" },
  { name: "Kitui", code: 15, capital: "Kitui Town" },
  { name: "Machakos", code: 16, capital: "Machakos Town" },
  { name: "Makueni", code: 17, capital: "Wote" },
  { name: "Nyandarua", code: 18, capital: "Ol Kalou" },
  { name: "Nyeri", code: 19, capital: "Nyeri Town" },
  { name: "Kirinyaga", code: 20, capital: "Kerugoya" },
  { name: "Murang'a", code: 21, capital: "Murang'a Town" },
  { name: "Kiambu", code: 22, capital: "Kiambu Town" },
  { name: "Turkana", code: 23, capital: "Lodwar" },
  { name: "West Pokot", code: 24, capital: "Kapenguria" },
  { name: "Samburu", code: 25, capital: "Maralal" },
  { name: "Trans-Nzoia", code: 26, capital: "Kitale" },
  { name: "Uasin Gishu", code: 27, capital: "Eldoret" },
  { name: "Elgeyo-Marakwet", code: 28, capital: "Iten" },
  { name: "Nandi", code: 29, capital: "Kapsabet" },
  { name: "Baringo", code: 30, capital: "Kabarnet" },
  { name: "Laikipia", code: 31, capital: "Nanyuki" },
  { name: "Nakuru", code: 32, capital: "Nakuru City" },
  { name: "Narok", code: 33, capital: "Narok Town" },
  { name: "Kajiado", code: 34, capital: "Kajiado Town" },
  { name: "Kericho", code: 35, capital: "Kericho Town" },
  { name: "Bomet", code: 36, capital: "Bomet Town" },
  { name: "Kakamega", code: 37, capital: "Kakamega Town" },
  { name: "Vihiga", code: 38, capital: "Vihiga Town" },
  { name: "Bungoma", code: 39, capital: "Bungoma Town" },
  { name: "Busia", code: 40, capital: "Busia Town" },
  { name: "Siaya", code: 41, capital: "Siaya Town" },
  { name: "Kisumu", code: 42, capital: "Kisumu City" },
  { name: "Homa Bay", code: 43, capital: "Homa Bay Town" },
  { name: "Migori", code: 44, capital: "Migori Town" },
  { name: "Kisii", code: 45, capital: "Kisii Town" },
  { name: "Nyamira", code: 46, capital: "Nyamira Town" },
  { name: "Nairobi", code: 47, capital: "Nairobi City" }
];

function CommsDashboardHome() {
  const [stats, setStats] = useState(null);
  const [recentNotifications, setRecentNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const [statsResponse, notificationsResponse] = await Promise.all([
        reportAPI.getDashboard(),
        notificationAPI.getRecent(4, true)
      ]);
      setStats(statsResponse.data);
      setRecentNotifications(notificationsResponse.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const performanceData = stats?.monthlyPerformance?.map(item => ({
    month: item.month_name,
    admissions: parseInt(item.admissions) || 0,
    enrollments: parseInt(item.enrollments) || 0
  })) || [
    { month: 'Jan', admissions: 0, enrollments: 0 },
    { month: 'Feb', admissions: 0, enrollments: 0 },
    { month: 'Mar', admissions: 0, enrollments: 0 },
    { month: 'Apr', admissions: 0, enrollments: 0 },
    { month: 'May', admissions: 0, enrollments: 0 },
    { month: 'Jun', admissions: 0, enrollments: 0 }
  ];

  const regionData = stats?.regionalData?.map((item, index) => ({
    name: item.region,
    value: parseInt(item.count) || 0,
    color: ['#037b90', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#ef4444', '#6366f1'][index % 10]
  })) || [{ name: 'No Data', value: 1, color: '#e2e8f0' }];

  if (loading) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <div className="stat-card-icon loading" style={{ width: '64px', height: '64px', fontSize: '28px', margin: '0 auto 24px' }}>📢</div>
          <h3 style={{ color: '#64748b', fontWeight: '600' }}>Loading Dashboard...</h3>
          <p style={{ color: '#94a3b8', marginTop: '8px' }}>Fetching latest marketing data and metrics</p>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Marketing Dashboard</h1>
          <p className="page-subtitle">Recruiter growth, admission trends, and regional performance data</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="btn btn-outline btn-sm" onClick={() => navigate('/comms/recruiters')}>
            👥 View Recruiters
          </button>
          <button className="btn btn-outline btn-sm" onClick={() => navigate('/comms/reports')}>
            📊 View Reports
          </button>
          <button className="btn btn-primary btn-sm" onClick={loadDashboard}>
            🔄 Refresh
          </button>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Active Recruiters</div>
            <div className="stat-card-icon" style={{ background: 'linear-gradient(135deg, #037b90, #0ea5e9)' }}>👥</div>
          </div>
          <div className="stat-card-value">{stats?.overview?.activeRecruiters || 0}</div>
          <div className="stat-card-change positive">Recruiters actively referring students</div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Total Admissions</div>
            <div className="stat-card-icon" style={{ background: 'linear-gradient(135deg, #10b981, #34d399)' }}>🎓</div>
          </div>
          <div className="stat-card-value">{stats?.overview?.totalAdmissions || 0}</div>
          <div className="stat-card-change positive">Students admitted via recruiters</div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Total Enrollments</div>
            <div className="stat-card-icon" style={{ background: 'linear-gradient(135deg, #f59e0b, #fbbf24)' }}>📚</div>
          </div>
          <div className="stat-card-value">{stats?.overview?.totalEnrollments || 0}</div>
          <div className="stat-card-change positive">Students enrolled and confirmed</div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Counties Reached</div>
            <div className="stat-card-icon" style={{ background: 'linear-gradient(135deg, #8b5cf6, #a78bfa)' }}>🗺️</div>
          </div>
          <div className="stat-card-value">{regionData.filter(r => r.name !== 'No Data').length || 0}</div>
          <div className="stat-card-change positive">Counties with active recruiters</div>
        </div>
      </div>

      <div className="dashboard-grid">
        <div className="chart-container">
          <div className="card-header">
            <div>
              <h2 className="card-title">Growth Trends</h2>
              <p className="card-subtitle">Monthly admissions and enrollments — last 6 months</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            {performanceData.some(item => item.admissions > 0 || item.enrollments > 0) ? (
              <AreaChart data={performanceData}>
                <defs>
                  <linearGradient id="admissionsGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#037b90" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#037b90" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="enrollmentsGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" stroke="#64748b" fontSize={12} fontWeight={600} />
                <YAxis stroke="#64748b" fontSize={12} fontWeight={600} />
                <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)', fontWeight: '600' }} />
                <Area type="monotone" dataKey="admissions" stroke="#037b90" strokeWidth={3} fill="url(#admissionsGradient)" name="Admissions" />
                <Area type="monotone" dataKey="enrollments" stroke="#10b981" strokeWidth={3} fill="url(#enrollmentsGradient)" name="Enrollments" />
              </AreaChart>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#64748b' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }}>📊</div>
                <h3 style={{ fontWeight: '600', marginBottom: '8px' }}>No Growth Data Yet</h3>
                <p style={{ fontSize: '14px', textAlign: 'center' }}>Admission and enrollment trends will appear here once data is available</p>
              </div>
            )}
          </ResponsiveContainer>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <div className="card">
            <div className="card-header">
              <div>
                <h2 className="card-title">Regional Distribution</h2>
                <p className="card-subtitle">Active recruiters by county</p>
              </div>
              <span style={{ fontSize: '10px', backgroundColor: '#037b90', color: 'white', padding: '2px 6px', borderRadius: '8px', fontWeight: '600' }}>🗺️ Live</span>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              {regionData[0].name !== 'No Data' ? (
                <PieChart>
                  <Pie data={regionData} cx="50%" cy="50%" innerRadius={40} outerRadius={80} paddingAngle={5} dataKey="value">
                    {regionData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', fontWeight: '600' }}
                    formatter={(value, name) => [`${value} recruiters`, name]} />
                </PieChart>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#64748b' }}>
                  <div style={{ fontSize: '32px', marginBottom: '12px', opacity: 0.5 }}>🗺️</div>
                  <h4 style={{ fontWeight: '600', marginBottom: '4px', fontSize: '14px' }}>No Regional Data</h4>
                  <p style={{ fontSize: '12px', textAlign: 'center' }}>Regional distribution will appear when recruiters are added</p>
                </div>
              )}
            </ResponsiveContainer>
            {regionData[0].name !== 'No Data' && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '16px' }}>
                {regionData.map((item, index) => (
                  <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                    <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: item.color }}></div>
                    <span style={{ fontWeight: '600', color: '#64748b' }}>{item.name} ({item.value})</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="card">
            <div className="card-header">
              <div>
                <h2 className="card-title">Quick Actions</h2>
                <p className="card-subtitle">Frequently used marketing tasks</p>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Link to="/comms/recruiters" className="btn btn-outline" style={{ justifyContent: 'flex-start', textDecoration: 'none' }}>👥 Browse All Recruiters</Link>
              <Link to="/comms/students" className="btn btn-outline" style={{ justifyContent: 'flex-start', textDecoration: 'none' }}>🎓 View Student Records</Link>
              <Link to="/comms/reports" className="btn btn-outline" style={{ justifyContent: 'flex-start', textDecoration: 'none' }}>📊 Generate Marketing Report</Link>
              <button className="btn btn-secondary" style={{ justifyContent: 'flex-start' }} onClick={loadDashboard}>🔄 Refresh Data</button>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div>
            <h2 className="card-title">Top Performing Recruiters</h2>
            <p className="card-subtitle">Highest earning recruiters — great for success stories and case studies</p>
          </div>
          <Link to="/comms/recruiters" className="btn btn-primary btn-sm" style={{ textDecoration: 'none' }}>👥 View All</Link>
        </div>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>#</th>
                <th>Recruiter</th>
                <th>Referral Code</th>
                <th>Admissions</th>
                <th>Enrollments</th>
                <th>Commission</th>
              </tr>
            </thead>
            <tbody>
              {stats?.topRecruiters?.length > 0 ? (
                stats.topRecruiters.map((recruiter, index) => (
                  <tr key={recruiter.id} onClick={() => navigate(`/comms/recruiters/${recruiter.id}`)} style={{ cursor: 'pointer' }}>
                    <td>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: '28px', height: '28px', borderRadius: '50%',
                        backgroundColor: index === 0 ? '#f59e0b' : index === 1 ? '#94a3b8' : index === 2 ? '#cd7f32' : '#f1f5f9',
                        color: index <= 2 ? 'white' : '#64748b',
                        fontWeight: '700', fontSize: '12px'
                      }}>
                        {index + 1}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{
                          width: '36px', height: '36px', borderRadius: '50%',
                          background: `linear-gradient(135deg, ${['#037b90', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'][index % 5]}, ${['#0ea5e9', '#34d399', '#fbbf24', '#a78bfa', '#f472b6'][index % 5]})`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: 'white', fontWeight: '700', fontSize: '14px'
                        }}>
                          {(recruiter.full_name || '?').split(' ').map(n => n[0]).join('').substring(0, 2)}
                        </div>
                        <div style={{ fontWeight: '600', fontSize: '14px' }}>{recruiter.full_name}</div>
                      </div>
                    </td>
                    <td><span className="badge badge-info">{recruiter.referral_code}</span></td>
                    <td><span style={{ fontWeight: '600' }}>{recruiter.admission_count}</span></td>
                    <td><span style={{ fontWeight: '600' }}>{recruiter.enrollment_count}</span></td>
                    <td><span style={{ fontWeight: '600', color: '#037b90' }}>KES {Number(recruiter.total_commission || 0).toFixed(2)}</span></td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>🏆</div>
                    <p>No recruiter data available yet</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div>
            <h2 className="card-title">Recent Activity</h2>
            <p className="card-subtitle">Latest system notifications and updates</p>
          </div>
          <Link to="/comms/notifications" className="btn btn-outline btn-sm">View All</Link>
        </div>
        <div>
          {recentNotifications.length > 0 ? (
            recentNotifications.map((notification) => (
              <div key={notification.id} className="activity-item">
                <div className="activity-title">{notification.title}</div>
                <div className="activity-description">{notification.message}</div>
                <div className="activity-time">{formatTimeAgo(notification.created_at)}</div>
              </div>
            ))
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#64748b' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }}>🔔</div>
              <h3 style={{ fontWeight: '600', marginBottom: '8px' }}>No Recent Activity</h3>
              <p style={{ fontSize: '14px' }}>System activities will appear here</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CommsViewRecruiters() {
  const [recruiters, setRecruiters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [countyFilter, setCountyFilter] = useState('');
  const [countyFilterSearch, setCountyFilterSearch] = useState('');
  const [showCountyDropdown, setShowCountyDropdown] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, limit: 10, page: 1 });
  const navigate = useNavigate();

  const filteredCounties = kenyanCountiesData.filter(c =>
    c.name.toLowerCase().includes(countyFilterSearch.toLowerCase())
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      loadRecruiters();
    }, 300);
    return () => clearTimeout(handler);
  }, [currentPage, searchTerm, countyFilter, categoryFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, countyFilter, categoryFilter]);

  useEffect(() => {
    apiCall('/recruiters/categories').then(res => setCategories(res.data)).catch(() => {});
  }, []);

  const loadRecruiters = async () => {
    try {
      const response = await recruiterAPI.getAll(currentPage, pagination.limit, searchTerm, countyFilter, categoryFilter);
      setRecruiters(response.data.recruiters || []);
      setPagination(response.data.pagination || { total: 0, limit: 10, page: 1 });
    } catch (error) {
      toast.error('Failed to load recruiters');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.comms-county-dropdown')) {
        setShowCountyDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Recruiters</h1>
          <p className="page-subtitle">View recruiter information for marketing and communications</p>
        </div>
      </div>

      <div className="card">
        <div style={{ display: 'flex', gap: '12px', padding: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="text"
            className="form-input"
            placeholder="🔍 Search by name, email, or code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ flex: '1', minWidth: '220px' }}
          />
          <div style={{ position: 'relative', minWidth: '200px' }} className="comms-county-dropdown">
            <div
              className="form-select"
              onClick={() => setShowCountyDropdown(!showCountyDropdown)}
              style={{ color: countyFilter ? '#1e293b' : '#94a3b8' }}
            >
              <span>{countyFilter || '📍 All Counties'}</span>
              <svg style={{ width: '14px', height: '14px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/></svg>
            </div>
            {showCountyDropdown && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, marginTop: '4px',
                background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: '280px', overflow: 'hidden',
                display: 'flex', flexDirection: 'column'
              }}>
                <div style={{ padding: '8px', borderBottom: '1px solid #e2e8f0' }}>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Search counties..."
                    value={countyFilterSearch}
                    onChange={(e) => setCountyFilterSearch(e.target.value)}
                    autoFocus
                  />
                </div>
                <div style={{ overflowY: 'auto', flex: 1 }}>
                  <div
                    onClick={() => { setCountyFilter(''); setCountyFilterSearch(''); setShowCountyDropdown(false); }}
                    style={{ padding: '10px 12px', cursor: 'pointer', fontSize: '14px', background: !countyFilter ? '#f0f9ff' : 'transparent', color: '#64748b', borderBottom: '1px solid #f1f5f9' }}
                  >
                    All Counties
                  </div>
                  {filteredCounties.map(county => (
                    <div
                      key={county.code}
                      onClick={() => { setCountyFilter(county.name); setCountyFilterSearch(''); setShowCountyDropdown(false); }}
                      style={{ padding: '10px 12px', cursor: 'pointer', fontSize: '14px', background: countyFilter === county.name ? '#f0f9ff' : 'transparent', borderBottom: '1px solid #f1f5f9' }}
                      onMouseEnter={(e) => e.target.style.background = countyFilter === county.name ? '#f0f9ff' : '#f8fafc'}
                      onMouseLeave={(e) => e.target.style.background = countyFilter === county.name ? '#f0f9ff' : 'transparent'}
                    >
                      {county.name}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div style={{ position: 'relative', minWidth: '160px' }}>
              <div
                className="form-select"
                onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                style={{ cursor: 'pointer', color: categoryFilter ? '#1e293b' : '#94a3b8' }}
              >
                <span>{categoryFilter ? (categories.find(c => c.name === categoryFilter)?.display_name || categoryFilter) : '📁 All Categories'}</span>
                <svg style={{ width: '14px', height: '14px', transition: 'transform 0.2s', transform: showCategoryDropdown ? 'rotate(180deg)' : 'none' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/></svg>
              </div>
              {showCategoryDropdown && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, marginTop: '4px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                  <div
                    onClick={() => { setCategoryFilter(''); setShowCategoryDropdown(false); }}
                    style={{ padding: '10px 12px', cursor: 'pointer', fontSize: '14px', background: !categoryFilter ? '#f0f9ff' : 'transparent', color: '#64748b', borderBottom: '1px solid #f1f5f9' }}
                  >
                    All Categories
                  </div>
                  {categories.map((cat) => (
                    <div
                      key={cat.id}
                      onClick={() => { setCategoryFilter(cat.name); setShowCategoryDropdown(false); }}
                      style={{ padding: '10px 12px', cursor: 'pointer', fontSize: '14px', background: categoryFilter === cat.name ? '#f0f9ff' : 'transparent', borderBottom: '1px solid #f1f5f9' }}
                      onMouseEnter={(e) => e.target.style.background = categoryFilter === cat.name ? '#f0f9ff' : '#f8fafc'}
                      onMouseLeave={(e) => e.target.style.background = categoryFilter === cat.name ? '#f0f9ff' : 'transparent'}
                    >
                      {cat.display_name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Referral Code</th>
                <th>County</th>
                <th>Category</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5" style={{ textAlign: 'center', padding: '40px' }}>Loading...</td></tr>
              ) : recruiters.length === 0 ? (
                <tr><td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>No recruiters found</td></tr>
              ) : (
                recruiters.map(r => (
                  <tr key={r.id} onClick={() => navigate(`/comms/recruiters/${r.id}`)} style={{ cursor: 'pointer' }}>
                    <td>
                      <div style={{ fontWeight: '600' }}>{r.full_name}</div>
                      <div style={{ fontSize: '13px', color: '#64748b' }}>{r.email}</div>
                    </td>
                    <td><span className="badge badge-info">{r.referral_code}</span></td>
                    <td>{r.county || 'N/A'}</td>
                    <td>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                        padding: '2px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 500,
                        background: r.category_name === 'student' ? '#dbeafe' : r.category_name === 'external' ? '#fef3c7' : r.category_name === 'alumni' ? '#e0f2fe' : '#f1f5f9',
                        color: r.category_name === 'student' ? '#1e40af' : r.category_name === 'external' ? '#92400e' : r.category_name === 'alumni' ? '#0369a1' : '#475569'
                      }}>
                        {r.category_display_name || r.category_name || 'Uncategorized'}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${r.status === 'active' ? 'badge-success' : 'badge-warning'}`}>
                        {r.status === 'active' ? '🟢 Active' : '🟡 Inactive'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          pagination={pagination}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          loading={loading}
        />
      </div>
    </div>
  );
}

function CommsViewStudents() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, limit: 10, page: 1 });
  const navigate = useNavigate();

  useEffect(() => {
    loadStudents();
  }, [currentPage, searchTerm]);

  const loadStudents = async () => {
    try {
      const response = await studentAPI.getAllStudents(currentPage, pagination.limit, '', false, searchTerm);
      setStudents(response.data.students || []);
      setPagination(response.data.pagination || { total: 0, limit: 10, page: 1 });
    } catch (error) {
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Student Records</h1>
          <p className="page-subtitle">View admitted and enrolled students for marketing materials</p>
        </div>
      </div>

      <div className="card">
        <div style={{ padding: '16px' }}>
          <input
            type="text"
            className="form-input"
            placeholder="🔍 Search by student name or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: '100%', maxWidth: '400px' }}
          />
        </div>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Student Name</th>
                <th>Student ID</th>
                <th>Course</th>
                <th>Admission Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5" style={{ textAlign: 'center', padding: '40px' }}>Loading...</td></tr>
              ) : students.length === 0 ? (
                <tr><td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>No student records found</td></tr>
              ) : (
                students.map(s => (
                  <tr key={s.student_id || s.id}>
                    <td style={{ fontWeight: '600' }}>{s.student_name || 'N/A'}</td>
                    <td>{s.student_id || 'N/A'}</td>
                    <td>{s.programme || 'N/A'}</td>
                    <td>{s.date ? new Date(s.date).toLocaleDateString() : 'N/A'}</td>
                    <td>
                      <span className={`badge ${s.status === 'Enrolled' ? 'badge-success' : s.status === 'Admitted' ? 'badge-info' : 'badge-warning'}`}>
                        {s.status === 'Enrolled' ? '✅ Enrolled' : s.status === 'Admitted' ? '✅ Admitted' : '⏳ Pending'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          pagination={pagination}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          loading={loading}
        />
      </div>
    </div>
  );
}

function CommsViewReports() {
  const [reportData, setReportData] = useState(null);
  const [activeReport, setActiveReport] = useState(null);
  const [generating, setGenerating] = useState(false);

  const generateReport = async (type) => {
    setGenerating(true);
    setActiveReport(type);
    try {
      const response = await reportAPI.getDashboard();
      setReportData(response.data || response);
    } catch (error) {
      toast.error('Failed to load report data');
    } finally {
      setGenerating(false);
    }
  };

  const handleBack = () => {
    setActiveReport(null);
    setReportData(null);
  };

  if (activeReport) {
    return (
      <ReportViewer
        reportData={reportData}
        activeReport={activeReport}
        onBack={handleBack}
        generating={generating}
      />
    );
  }

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Reports</h1>
          <p className="page-subtitle">Generate and export reports for marketing campaigns</p>
        </div>
      </div>
      <ReportCards onGenerate={generateReport} />
    </div>
  );
}

function CommsNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, limit: 20, page: 1 });
  const navigate = useNavigate();

  const [draftOpen, setDraftOpen] = useState(false);
  const [draftChannel, setDraftChannel] = useState('sms');
  const [draftContext, setDraftContext] = useState('');
  const [draftTone, setDraftTone] = useState('professional');
  const [draftLanguage, setDraftLanguage] = useState('English');
  const [draftCount, setDraftCount] = useState(2);
  const [drafts, setDrafts] = useState([]);
  const [drafting, setDrafting] = useState(false);

  const labelStyle = {
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '8px',
    display: 'block'
  };

  const generateDrafts = async () => {
    setDrafting(true);
    try {
      const res = await aiAPI.draftMessage({
        channel: draftChannel,
        context: draftContext,
        tone: draftTone,
        language: draftLanguage,
        count: draftCount
      });
      setDrafts(res.data?.drafts || []);
      toast.success('Drafts generated');
    } catch (e) {
      toast.error(e.message || 'Failed to draft');
    } finally {
      setDrafting(false);
    }
  };

  useEffect(() => {
    loadNotifications();
  }, [currentPage]);

  const loadNotifications = async () => {
    try {
      const response = await notificationAPI.getAll({ page: currentPage, limit: pagination.limit, admin: true });
      setNotifications(response.data?.notifications || response.data || []);
      setPagination(response.data?.pagination || { total: 0, limit: 20, page: 1 });
    } catch (error) {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Notifications</h1>
          <p className="page-subtitle">System updates and activity alerts</p>
        </div>
        <div>
          <button className="btn btn-primary btn-sm" onClick={() => { setDrafts([]); setDraftOpen(true); }}>
            <Icon name="help" /> Draft with AI
          </button>
        </div>
      </div>

      {draftOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '16px' }}>
          <div style={{ background: 'var(--card-background)', borderRadius: 'var(--radius-lg)', width: '520px', maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--shadow-xl)' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: '16px' }}><Icon name="help" /> Draft with AI</h2>
              <button className="btn btn-outline btn-sm" onClick={() => setDraftOpen(false)}><Icon name="close" /></button>
            </div>
            <div style={{ padding: '20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px' }}>
              <div>
                <label style={labelStyle}>Channel</label>
                <select className="form-select" value={draftChannel} onChange={(e) => setDraftChannel(e.target.value)}>
                  <option value="sms">SMS</option>
                  <option value="email">Email</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Tone</label>
                <select className="form-select" value={draftTone} onChange={(e) => setDraftTone(e.target.value)}>
                  <option value="professional">Professional</option>
                  <option value="friendly">Friendly</option>
                  <option value="urgent">Urgent</option>
                  <option value="formal">Formal</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Language</label>
                <select className="form-select" value={draftLanguage} onChange={(e) => setDraftLanguage(e.target.value)}>
                  <option value="English">English</option>
                  <option value="Swahili">Swahili</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Variants</label>
                <input type="number" className="form-input" min={1} max={5} value={draftCount} onChange={(e) => setDraftCount(Math.min(5, Math.max(1, parseInt(e.target.value, 10) || 1)))} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Context / Key points</label>
                <textarea className="form-input" rows={3} value={draftContext} onChange={(e) => setDraftContext(e.target.value)} placeholder="e.g., September 2026 intake is open; encourage applicants to apply early" />
              </div>
            </div>
            <div style={{ padding: '0 20px 20px' }}>
              <button className="btn btn-primary" onClick={generateDrafts} disabled={drafting} style={{ width: '100%' }}>
                {drafting ? 'Generating…' : 'Generate Drafts'}
              </button>
              {drafts.length > 0 && (
                <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {drafts.map((d, i) => (
                    <div key={i} style={{ border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '12px' }}>
                      <p style={{ margin: '0 0 8px', whiteSpace: 'pre-wrap' }}>{d}</p>
                      <button className="btn btn-outline btn-sm" onClick={() => { navigator.clipboard.writeText(d); toast.success('Copied'); }}>
                        <Icon name="copy" /> Copy
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="card">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
        ) : notifications.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#64748b' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }}>🔔</div>
            <h3 style={{ fontWeight: '600', marginBottom: '8px' }}>No Notifications</h3>
            <p style={{ fontSize: '14px' }}>You're all caught up!</p>
          </div>
        ) : (
          notifications.map(n => (
            <div key={n.id} className="activity-item">
              <div className="activity-title">{n.title}</div>
              <div className="activity-description">{n.message}</div>
              <div className="activity-time">{new Date(n.created_at).toLocaleString()}</div>
            </div>
          ))
        )}
        <Pagination
          pagination={pagination}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          loading={loading}
        />
      </div>
    </div>
  );
}

function CommsViewRecruiterDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [recruiter, setRecruiter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('students');

  useEffect(() => {
    loadRecruiter();
  }, [id]);

  const loadRecruiter = async () => {
    try {
      const response = await recruiterAPI.get(id);
      setRecruiter(response.data || response);
    } catch (error) {
      toast.error(error.message || 'Failed to load recruiter');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <p>Loading recruiter details...</p>
        </div>
      </div>
    );
  }

  if (!recruiter) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <p>Recruiter not found</p>
          <button className="btn btn-primary" onClick={() => navigate('/comms/recruiters')}>Back to Recruiters</button>
        </div>
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'students':
        return <CommsStudentsTab students={recruiter.students || []} />;
      case 'admissions':
        return <CommsAdmissionsTab admissions={recruiter.admissions || []} />;
      case 'enrollments':
        return <CommsEnrollmentsTab enrollments={recruiter.enrollments || []} />;
      case 'payments':
        return <CommsPaymentsTab payments={recruiter.payments || []} />;
      default:
        return null;
    }
  };

  return (
    <div className="main-content">
      <div className="dashboard-header">
        <div>
          <h1>{recruiter.full_name}</h1>
          <p style={{ color: '#64748b', margin: 0 }}>
            Referral Code: {recruiter.referral_code} | 
            Email: {recruiter.email}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-secondary" onClick={() => navigate('/comms/recruiters')}>
            Back to Recruiters
          </button>
        </div>
      </div>

      <div className="dashboard-grid" style={{ marginBottom: '24px' }}>
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center' }}>
            <h3 style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>Total Admissions</h3>
            <p className="stat-value" style={{ margin: '8px 0 0' }}>{recruiter.total_admissions || 0}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center' }}>
            <h3 style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>Total Enrollments</h3>
            <p className="stat-value" style={{ margin: '8px 0 0' }}>{recruiter.total_enrollments || 0}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center' }}>
            <h3 style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>Total Earnings</h3>
            <p className="stat-value" style={{ margin: '8px 0 0' }}>KES {Number(recruiter.total_earned || 0).toFixed(2)}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center' }}>
            <h3 style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>Payment Status</h3>
            <p className="stat-value" style={{ margin: '8px 0 0' }}>
              <span className={`status-badge ${recruiter.payment_status || 'pending'}`}>
                {recruiter.payment_status || 'pending'}
              </span>
            </p>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="card-header">
          <h3>Personal Information</h3>
        </div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>Full Name</label>
              <p style={{ margin: '4px 0' }}>{recruiter.full_name}</p>
            </div>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>Mobile Number</label>
              <p style={{ margin: '4px 0' }}>{recruiter.mobile_number}</p>
            </div>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>Nationality</label>
              <p style={{ margin: '4px 0' }}>{recruiter.nationality}</p>
            </div>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>ID/Passport</label>
              <p style={{ margin: '4px 0' }}>{recruiter.id_passport}</p>
            </div>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>KRA PIN</label>
              <p style={{ margin: '4px 0' }}>{recruiter.kra_pin || 'Not provided'}</p>
            </div>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>Status</label>
              <p style={{ margin: '4px 0' }}>
                <span className={`status-badge ${recruiter.status}`}>{recruiter.status}</span>
              </p>
            </div>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>County</label>
              <p style={{ margin: '4px 0' }}>{recruiter.county}</p>
            </div>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>Subcounty</label>
              <p style={{ margin: '4px 0' }}>{recruiter.subcounty || 'Not provided'}</p>
            </div>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>Residential Address</label>
              <p style={{ margin: '4px 0' }}>{recruiter.residential_address}</p>
            </div>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>Bank Account</label>
              <p style={{ margin: '4px 0' }}>{recruiter.bank_account || 'Not provided'}</p>
            </div>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>Bank Name</label>
              <p style={{ margin: '4px 0' }}>{recruiter.bank_name || 'Not provided'}</p>
            </div>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>Branch Code</label>
              <p style={{ margin: '4px 0' }}>{recruiter.branch_code || 'Not provided'}</p>
            </div>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>Referral Code</label>
              <p style={{ margin: '4px 0' }}>{recruiter.referral_code}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header" style={{ borderBottom: 'none', paddingBottom: 0 }}>
          <div className="tabs" style={{ display: 'flex', gap: '4px', borderBottom: '2px solid #e2e8f0', width: '100%' }}>
            {[
              { id: 'students', label: 'Students', count: (recruiter.students || []).length },
              { id: 'admissions', label: 'Admissions', count: (recruiter.admissions || []).length },
              { id: 'enrollments', label: 'Enrollments', count: (recruiter.enrollments || []).length },
              { id: 'payments', label: 'Payments', count: (recruiter.payments || []).length }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  fontWeight: activeTab === tab.id ? 600 : 400,
                  color: activeTab === tab.id ? '#037b90' : '#64748b',
                  borderBottom: activeTab === tab.id ? '2px solid #037b90' : '2px solid transparent',
                  marginBottom: '-2px',
                  fontSize: '14px'
                }}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>
        </div>
        <div className="card-body" style={{ paddingTop: '20px' }}>
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
}

function CommsStudentsTab({ students }) {
  if (students.length === 0) {
    return <p style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>No students recruited yet</p>;
  }
  return (
    <div className="table-container">
    <table className="table">
      <thead>
        <tr>
          <th>Student Name</th>
          <th>Student ID</th>
          <th>Course</th>
          <th>Admission Date</th>
          <th>Verified</th>
          <th>Commission</th>
        </tr>
      </thead>
      <tbody>
        {students.map((student) => (
          <tr key={student.id}>
            <td>{student.student_name}</td>
            <td>{student.student_id}</td>
            <td>{student.course || 'N/A'}</td>
            <td>{student.admission_date ? new Date(student.admission_date).toLocaleDateString() : 'N/A'}</td>
            <td>
              <span className={`status-badge ${student.admission_verified ? 'active' : 'pending'}`}>
                {student.admission_verified ? 'Verified' : 'Pending'}
              </span>
            </td>
            <td>KES {Number(student.commission_amount || 0).toFixed(2)}</td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}

function CommsAdmissionsTab({ admissions }) {
  if (admissions.length === 0) {
    return <p style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>No admissions recorded</p>;
  }
  return (
    <div className="table-container">
    <table className="table">
      <thead>
        <tr>
          <th>Student Name</th>
          <th>Student ID</th>
          <th>Admission Date</th>
          <th>Verified</th>
          <th>Commission</th>
        </tr>
      </thead>
      <tbody>
        {admissions.map((admission) => (
          <tr key={admission.id}>
            <td>{admission.student_name || 'N/A'}</td>
            <td>{admission.student_id}</td>
            <td>{new Date(admission.admission_date).toLocaleDateString()}</td>
            <td>
              <span className={`status-badge ${admission.admission_verified ? 'active' : 'pending'}`}>
                {admission.admission_verified ? 'Verified' : 'Pending'}
              </span>
            </td>
            <td>KES {Number(admission.commission_amount || 500).toFixed(2)}</td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}

function CommsEnrollmentsTab({ enrollments }) {
  if (enrollments.length === 0) {
    return <p style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>No enrollments recorded</p>;
  }
  return (
    <div className="table-container">
    <table className="table">
      <thead>
        <tr>
          <th>Student Name</th>
          <th>Student ID</th>
          <th>Enrollment Date</th>
          <th>Verified</th>
          <th>Commission</th>
        </tr>
      </thead>
      <tbody>
        {enrollments.map((enrollment) => (
          <tr key={enrollment.id}>
            <td>{enrollment.student_name || 'N/A'}</td>
            <td>{enrollment.student_id}</td>
            <td>{new Date(enrollment.enrollment_date).toLocaleDateString()}</td>
            <td>
              <span className={`status-badge ${enrollment.enrollment_verified ? 'active' : 'pending'}`}>
                {enrollment.enrollment_verified ? 'Verified' : 'Pending'}
              </span>
            </td>
            <td>KES {Number(enrollment.commission_amount || 300).toFixed(2)}</td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}

function CommsPaymentsTab({ payments }) {
  if (payments.length === 0) {
    return <p style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>No payment records yet</p>;
  }
  return (
    <div className="table-container">
    <table className="table">
      <thead>
        <tr>
          <th>Date</th>
          <th>Admissions</th>
          <th>Enrollments</th>
          <th>Total Amount</th>
          <th>Status</th>
          <th>Reference</th>
        </tr>
      </thead>
      <tbody>
        {payments.map((payment) => (
          <tr key={payment.id}>
            <td>{payment.created_at ? new Date(payment.created_at).toLocaleDateString() : 'N/A'}</td>
            <td>{payment.total_admissions || 0}</td>
            <td>{payment.total_enrollments || 0}</td>
            <td>KES {Number(payment.cumulative_amount || 0).toFixed(2)}</td>
            <td>
              <span className={`status-badge ${payment.payment_status || 'pending'}`}>
                {payment.payment_status || 'pending'}
              </span>
            </td>
            <td>{payment.payment_reference || 'N/A'}</td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}

function CommsMarketingDashboard() {
  return (
    <Routes>
      <Route path="/" element={<CommsAnalyticsDashboard />} />
      <Route path="/recruiters" element={<CommsViewRecruiters />} />
      <Route path="/recruiters/:id" element={<CommsViewRecruiterDetails />} />
      <Route path="/students" element={<CommsViewStudents />} />
      <Route path="/reports" element={<CommsViewReports />} />
      <Route path="/notifications" element={<CommsNotifications />} />
    </Routes>
  );
}

export default CommsMarketingDashboard;
